import axios from 'axios';

// The Admin page's Transcriptions tab, talking to backend/blueprints/notice_transcripts.py.
// The transcribing itself happens on a worker on somebody's own machine (transcriber/), so
// everything here is a queue seen from the browser: put a recording on it, watch it move,
// read what came back.

export type TranscriptStatus = 'uploading' | 'queued' | 'running' | 'done' | 'failed';

export type Transcript = {
  id: number;
  title: string;
  filename: string;
  bytes: number;
  duration: number | null;
  language: string | null;
  speakers: number | null;
  prompt: string | null;
  status: TranscriptStatus;
  error: string | null;
  // 0 to 1 while running, as the worker last reported it; null before it has.
  progress: number | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
};

export type Turn = { speaker: string; start: number; end: number; text: string };

export type TranscriptFull = Transcript & { turns: Turn[]; names: Record<string, string> };

export type Worker = { seenAt: string | null; host: string | null } | null;

// This account's worker key, as the server can still describe it: never the key itself.
export type TranscriberKeyInfo = { preview: string | null; createdAt: string | null; lastUsedAt: string | null };

// Under the sandbox's nginx limit (25m) with room for the request around it — the server's
// MAX_CHUNK. A 40-minute meeting is two of them.
const CHUNK = 20 * 1024 * 1024;

export type NewTranscript = {
  file: File;
  title: string;
  language: string;
  speakers: number | null;
  prompt: string;
};

// Open the meeting, send the file a chunk at a time, then queue it. Each chunk names its
// offset and the server only appends at the end of what it has, so a retry after a lost
// answer is harmless; `received` in a 409 says where to pick up.
export async function uploadTranscript(
  input: NewTranscript, onProgress: (fraction: number) => void,
): Promise<Transcript> {
  const { file } = input;
  const { data: created } = await axios.post<Transcript>('/api/notice/transcripts', {
    filename: file.name,
    bytes: file.size,
    title: input.title,
    language: input.language || null,
    speakers: input.speakers,
    prompt: input.prompt,
  });
  let offset = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + CHUNK);
    try {
      const { data } = await axios.put<{ received: number }>(
        `/api/notice/transcripts/${created.id}/audio`, chunk,
        { params: { offset }, headers: { 'Content-Type': 'application/octet-stream' } },
      );
      offset = data.received;
    } catch (e) {
      const received = axios.isAxiosError(e) ? e.response?.data?.received : undefined;
      if (typeof received !== 'number' || received === offset) throw e;
      offset = received;
    }
    onProgress(offset / file.size);
  }
  const { data } = await axios.post<Transcript>(`/api/notice/transcripts/${created.id}/queue`);
  return data;
}

// "01:02:03", or "02:03" under an hour.
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return hh ? `${String(hh).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
}

// The name somebody gave a speaker, or the model's SPEAKER_00 as "Speaker 1" — `word` is
// that "Speaker" in the page's language.
export const speakerName = (names: Record<string, string>, speaker: string, word: string) =>
  names[speaker] || speaker.replace(/^SPEAKER_0*(\d+)$/, (_, n) => `${word} ${Number(n) + 1}`);

// The same text the command-line script writes: one paragraph a turn, stamped.
export function transcriptText(t: TranscriptFull, word: string): string {
  return t.turns
    .map((turn) => `[${clock(turn.start)}] ${speakerName(t.names, turn.speaker, word)}: ${turn.text}`)
    .join('\n\n') + '\n';
}
